import { useEffect, useRef } from 'react';

import { PROCESSED_NOTIFICATION_KINDS } from 'cpool-constants';

import { useNewDocuments } from '~/features/dashboard/hooks/useNewDocuments';
import { useResubmissions } from '~/features/dashboard/hooks/useResubmissions';
import { useUnreadConsorsNotes } from '~/features/dashboard/hooks/useUnreadConsorsNotes';
import { beginningOfTime } from '~/features/dashboard/utils/resubmissionDateRange';
import { useMarkNotificationProcessed } from '~/features/notifications/hooks/useMarkNotificationProcessed';
import { type Baseline, emptyBaseline, readBaseline, writeBaseline } from '~/features/notifications/utils/baseline';
import { buildBankReportRecord } from '~/features/notifications/utils/buildBankReportRecord';
import { buildDocumentList } from '~/features/notifications/utils/buildDocumentList';
import { buildResubmissionRecord } from '~/features/notifications/utils/buildResubmissionRecord';
import useUserInfo from '~/hooks/useUserInfo';

/**
 * Watches the three notification hooks and records a processed notification in the backend whenever an
 * item disappears between two snapshots. The baseline (the previous snapshot) is persisted in
 * localStorage per user purely as diff state, so disappearances are still detected across reloads — the
 * processed records themselves now live in the backend read model.
 *
 * IMPORTANT: this hook MUST be mounted exactly once per app — the Header mounts it at the root level via
 * `ProcessedNotificationsTracker` (only for roles that show the bell) so that the duplicate `headerButtons`
 * fragment (desktop + mobile menu) doesn't run two trackers in parallel and emit duplicate commands.
 */
export const useTrackProcessedNotifications = () => {
    const userInfo = useUserInfo();
    const userId = userInfo?.id ?? null;
    const { mutate: markProcessed } = useMarkNotificationProcessed();

    const resubmissions = useResubmissions({ start: beginningOfTime() });
    const newDocuments = useNewDocuments();
    const unreadConsorsNotes = useUnreadConsorsNotes();

    const baselineRef = useRef<Baseline | null>(null);

    useEffect(() => {
        if (userId) baselineRef.current = readBaseline(userId);
    }, [userId]);

    useEffect(() => {
        if (!userId || !resubmissions.isSuccess || !resubmissions.data) return;
        const baseline = baselineRef.current ?? emptyBaseline();
        const next = buildResubmissionRecord(resubmissions.data);
        Object.entries(baseline.resubmissions)
            .filter(([loanRequestId]) => !(loanRequestId in next))
            .forEach(([loanRequestId, resubmissionAt]) => {
                markProcessed({ kind: PROCESSED_NOTIFICATION_KINDS.RESUBMISSION, loanRequestId, resubmissionAt });
            });
        const updated: Baseline = { ...baseline, resubmissions: next };
        baselineRef.current = updated;
        writeBaseline(userId, updated);
    }, [resubmissions.data, resubmissions.isSuccess, markProcessed, userId]);

    useEffect(() => {
        if (!userId || !newDocuments.isSuccess || !newDocuments.data) return;
        const baseline = baselineRef.current ?? emptyBaseline();
        const next = buildDocumentList(newDocuments.data);
        const nextSet = new Set(next);
        baseline.documents
            .filter((loanRequestId) => !nextSet.has(loanRequestId))
            .forEach((loanRequestId) => {
                markProcessed({ kind: PROCESSED_NOTIFICATION_KINDS.DOCUMENT, loanRequestId });
            });
        const updated: Baseline = { ...baseline, documents: next };
        baselineRef.current = updated;
        writeBaseline(userId, updated);
    }, [newDocuments.data, newDocuments.isSuccess, markProcessed, userId]);

    useEffect(() => {
        if (!userId || !unreadConsorsNotes.isSuccess || !unreadConsorsNotes.data) return;
        const baseline = baselineRef.current ?? emptyBaseline();
        const next = buildBankReportRecord(unreadConsorsNotes.data);
        Object.entries(baseline.bankReports)
            .filter(([loanRequestId]) => !(loanRequestId in next))
            .forEach(([loanRequestId, subscriptionIdentifier]) => {
                markProcessed({
                    kind: PROCESSED_NOTIFICATION_KINDS.BANK_REPORT,
                    loanRequestId,
                    subscriptionIdentifier,
                });
            });
        const updated: Baseline = { ...baseline, bankReports: next };
        baselineRef.current = updated;
        writeBaseline(userId, updated);
    }, [unreadConsorsNotes.data, unreadConsorsNotes.isSuccess, markProcessed, userId]);
};
